// Combat tuning values (Phase 5 Step 9).
//
// Composed back into `balance` via balance.ts as:
//   combat: balanceCombat.combat
//
// Per DECISIONS 2026-04-18 "Phase 4 Step 18 balance-*.ts sibling-file
// split": each new subsystem's tuning lives in a balance-<subsystem>.ts
// sibling, composed via property assignment (not spread). This file is
// the canonical home for combat tick, damage, cooldown, durability,
// repair, and boss-ability tuning knobs.
//
// Data-catalog values (per-ability cooldowns/damage, per-mob stats,
// archetype geometry, loot tables) stay in shared/abilities.ts,
// shared/mob-types.ts, and shared/loot-tables.ts respectively — same
// reasoning as shared/recipes.ts. This file is for cross-entity tuning
// knobs only: defaults, multipliers, costs, cadence.

export const balanceCombat = {
  combat: {
    // Basic-attack defaults. The ability catalog (shared/abilities.ts)
    // carries the real per-ability cooldown/damage; these are the
    // canonical "no gear, unarmed melee" values referenced by generic
    // code paths (hit-resolution fallback, test fixtures).
    basicAttackCooldownMs: 1000,
    basicAttackDamageBase: 5,
    basicAttackDamageVariance: 2,
    basicAttackRange: 80,

    // Mob tick cadence — ExpeditionDO alarm arms at this interval while
    // an expedition is active. Per-mob tickMs in shared/mob-types.ts
    // is read by the mob state machine to gate attack rate; this is
    // the coordinator's wake frequency.
    mobTickMs: 500,
    // Default aggro range used by generic lookup paths; per-mob override
    // in shared/mob-types.ts is authoritative for actual AI decisions.
    mobAggroRange: 100,

    // Durability + repair knobs (Step 10 consumes).
    // Wear amount applied to the equipped weapon per successful hit.
    weaponWearPerHit: 1,
    // Multiplier on damage when equipped weapon has durability_current === 0.
    // Does NOT destroy the weapon — economy design pillar is low-stakes.
    brokenWeaponDamageMultiplier: 0.5,
    // Repair cost in repair-kit items + credits; restores durability_current
    // to durability_max. Step 10 wires the Repair tab RPC flow.
    repairMaterialCost: 2,
    repairCreditCost: 10,

    // Hit roll: Math.random() < hitChance → hit. Tests inject a seeded
    // RNG via Host.random to make outcomes deterministic without
    // changing production behavior.
    hitChance: 0.95,

    // Respawn delay after death. Step 9 schedules the respawn via a
    // deferred-task mechanism; dead player stays dead for this long
    // before ExpeditionDO restores HP + entry position.
    respawnDelayMs: 5000,

    // Expedition abandon TTL. If no activity (ability use / damage /
    // entry) for this long, the expedition alarm fires abandonExpedition.
    // Step 14 wires consumption; value lives here.
    expeditionAbandonTimeoutMs: 30 * 60 * 1000,

    // Grace window for the zero-socket shortcut in expedition-alarm.ts.
    // An active expedition with no connected sockets waits this long
    // before triggering the immediate-abandon path, allowing brief
    // reconnect windows (reconnectInitialMs ≈ 1s) to survive without
    // clearing current_expedition_id on all members. 15s covers
    // the 1s reconnect delay + test sleep + harness round-trip with
    // headroom.
    zeroSocketGracePeriodMs: 15_000,

    // Default HP values used when seeding expedition_member on entry
    // (Step 12) and when a PlayerDO row lacks an explicit HP.
    defaultPlayerMaxHp: 100,
    // Generic default fallback for mobs without a catalog entry — the
    // per-mob maxHp in shared/mob-types.ts is authoritative; this is a
    // safety value for any lookup that misses.
    defaultMobMaxHp: 30,
    // Fallback boss HP; derelict-boss in mob-types.ts overrides via
    // its own maxHp.
    defaultBossMaxHp: 300,

    // Boss unique ability — derelict-boss 'boss-cone-slam' simplified
    // to circular AoE in Phase 5 per Step 9 pre-decision (cone-shape
    // UI is Phase 6). The slam damages every live player within
    // bossConeSlamRadius.
    bossConeSlamCooldownMs: 8000,
    bossConeSlamDamageBase: 20,
    bossConeSlamDamageVariance: 5,
    bossConeSlamRadius: 150,
  },
} as const;
